import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, X } from 'lucide-react';
import { VehicleTypes } from '@hero/schema';
import { CheckIcon, VehicleDynamicIcon } from '@hero/web-icons';
import { useVehicleOptions } from '@hero/hooks-shared';
import { cn } from '@hero/web-lib';
import { Button } from '../../ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/Popover';
import { Input } from '../../ui/Input';

export interface VehicleTypeMultiSelectorProps {
  value: VehicleTypes[];
  onChange: (next: VehicleTypes[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Whether to render the picker inside a Radix Portal. */
  disablePortal?: boolean;
  /**
   * Trigger density.
   * - `sm` (32px) — table cells.
   * - `md` (40px, default) — standard form inputs.
   * - `lg` (56px) — "card" rows where the selector lines up next to
   *   other 56px-tall siblings (e.g. counter rows, the reorder modal's
   *   pickup-date button).
   */
  size?: 'sm' | 'md' | 'lg';
  /** Hide the secondary X clear button. */
  hideClear?: boolean;
}

/**
 * Multi-select for vehicle types. Renders a combobox-style trigger that opens
 * a popover with a search input and grouped checkbox list (mirrors how the
 * order publish flow already groups trucks / lorries / dynas).
 *
 * The selection is reported as an array of `VehicleTypes`, ready to drop into
 * the `RouteCreateAPI.vehicles[]` payload.
 */
export function VehicleTypeMultiSelector({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  disablePortal = false,
  size = 'md',
  hideClear = false,
}: VehicleTypeMultiSelectorProps) {
  const isSm = size === 'sm';
  const isLg = size === 'lg';
  const { t } = useTranslation(['m-vehicle']);
  const { groupedVehicles } = useVehicleOptions();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id: VehicleTypes) => {
    if (selectedSet.has(id)) {
      onChange(value.filter(v => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const clear = () => onChange([]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return Object.entries(groupedVehicles).map(([groupKey, vehicles]) => {
      const filtered = vehicles.filter(v => {
        if (!term) return true;
        const label = t(`types.${v.id}` as never) as unknown as string;
        return label.toLowerCase().includes(term) || v.id.includes(term);
      });
      return { groupKey, vehicles: filtered };
    });
  }, [groupedVehicles, search, t]);

  const hasResults = filteredGroups.some(g => g.vehicles.length > 0);

  return (
    // `min-w-0` is required on every link of the flex chain (wrapper →
    // button → inner span) so a long list of selected chips actually
    // truncates instead of pushing the trigger past its column. Without
    // it the chips visually bleed into neighbouring grid columns
    // (e.g. the reorder-modal pickup-date cell).
    <div className={cn('flex items-stretch gap-1 min-w-0 max-w-full', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            disabled={disabled}
            className={cn(
              'flex-1 min-w-0 justify-between gap-1.5 text-start font-normal',
              isSm ? 'h-8 px-2 text-xs' : isLg ? 'h-14 px-3 rounded-lg' : 'h-10 px-3',
              value.length === 0 && 'text-muted-foreground',
            )}>
            <span className="flex flex-1 items-center gap-1.5 min-w-0 overflow-hidden">
              {value.length === 0 ? (
                <span className="truncate">
                  {placeholder ??
                    t('selector.types_placeholder', {
                      defaultValue: 'Pick vehicle types',
                    })}
                </span>
              ) : (
                // Card-style (`lg`) triggers usually live in 1/3-width grid
                // cells, so we cap visible chips at 1 there to leave room
                // for the `+N` overflow badge. `sm` (table cells) already
                // had a tight cap.
                <SelectedVehicleChips
                  ids={value}
                  max={isSm ? 1 : isLg ? 1 : 3}
                  compact={isSm || isLg}
                />
              )}
            </span>
            <ChevronsUpDown
              className={cn('opacity-50 flex-shrink-0', isSm ? 'size-3' : 'size-4')}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          disablePortal={disablePortal}
          className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]">
          <div className="border-b px-3 py-2 bg-white">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('selector.search_placeholder', {
                defaultValue: 'Search vehicles…',
              })}
              className="h-8 border-0 px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto py-1">
            {!hasResults && (
              <p className="px-4 py-6 text-center text-sm text-gray-500">
                {t('selector.no_vehicle_types', { defaultValue: 'No vehicle types match.' })}
              </p>
            )}
            {filteredGroups.map(g =>
              g.vehicles.length === 0 ? null : (
                <div key={g.groupKey} className="py-1">
                  <div className="px-3 pt-1 pb-1 text-[11px] uppercase tracking-wide text-gray-400">
                    {t(`groups.${g.groupKey}` as never, { defaultValue: g.groupKey })}
                  </div>
                  {g.vehicles.map(v => {
                    const id = v.id as VehicleTypes;
                    const isChecked = selectedSet.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        role="option"
                        aria-selected={isChecked}
                        onClick={() => toggle(id)}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2 text-start hover:bg-gray-50',
                          isChecked && 'bg-gray-50',
                        )}>
                        {/*
                          Visual-only checkbox indicator. We can't use the
                          shared Radix `Checkbox` here because it renders a
                          `<button>` and we'd end up with a button-in-button
                          hydration error inside this row trigger.
                        */}
                        <span
                          aria-hidden
                          className={cn(
                            'flex size-4 flex-shrink-0 items-center justify-center rounded-sm border border-primary',
                            isChecked ? 'bg-primary text-white' : 'bg-white',
                          )}>
                          {isChecked && <CheckIcon className="size-3" />}
                        </span>
                        <div className="flex size-7 items-center justify-center rounded-md border bg-white">
                          <VehicleDynamicIcon
                            vehicle={id}
                            size="small"
                            className="size-4 text-gray-700"
                          />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {t(`types.${id}`)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ),
            )}
          </div>
        </PopoverContent>
      </Popover>

      {value.length > 0 && !disabled && !hideClear && (
        <Button
          type="button"
          variant="outline"
          className={cn(isSm ? 'h-8 px-1.5' : isLg ? 'h-14 px-2' : 'px-2')}
          onClick={clear}
          aria-label={t('selector.clear_types', { defaultValue: 'Clear vehicles' })}>
          <X className={cn(isSm ? 'size-3' : 'size-4')} />
        </Button>
      )}
    </div>
  );
}

interface SelectedVehicleChipsProps {
  ids: VehicleTypes[];
  max?: number;
  compact?: boolean;
}

function SelectedVehicleChips({
  ids,
  max = 3,
  compact = false,
}: SelectedVehicleChipsProps) {
  const { t } = useTranslation('m-vehicle');
  const visible = ids.slice(0, max);
  const remaining = ids.length - visible.length;

  return (
    <span className="flex items-center gap-1 min-w-0">
      {visible.map(id => (
        <span
          key={id}
          className={cn(
            'inline-flex min-w-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700',
            compact && 'bg-gray-100/80',
          )}>
          <VehicleDynamicIcon
            vehicle={id}
            size="small"
            className="size-3 flex-shrink-0 text-gray-600"
          />
          <span className="truncate">{t(`types.${id}`)}</span>
        </span>
      ))}
      {remaining > 0 && (
        <span className="inline-flex flex-shrink-0 items-center rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
          +{remaining}
        </span>
      )}
    </span>
  );
}
